// YOU ARE HERE — the player's own avatar, standing on whatever they are currently at.
//
// Whereabouts are a fact about the save file (whereabouts.mjs); this is the only place they are a thing you can
// SEE. Without it the distinction between being somewhere and looking at it is a rule the game enforces and never
// shows, which is the worst of both: refusals with no explanation on screen.
//
// CONSTANT SCREEN SIZE, and that is a considered choice rather than a shortcut. The same marker has to read over a
// galaxy three pixels wide in the universe view and over a planet you can orbit in the system view — a span of
// maybe six orders of magnitude. Drawn to scale it is either invisible at one end or absurd at the other; drawn at
// a fixed pixel height it is legible everywhere and honest about being a marker rather than a figure standing in
// space.
//
// JUST THE FIGURE. There was a ring on the object and a leader line up to the feet; both are gone. The figure
// alone is the marker, and the standoff below it is now only a GAP — enough that the suit reads against the
// object's silhouette instead of sinking into it, and the room a label needs to clear it.
import * as THREE from '/lib/three/three.module.min.js';
import { buildSuit, SUIT_LOCAL_HEIGHT, SUIT_LOCAL_FEET } from './suit.js';
import { markNow } from './bracket.js';   // the marks' own clock — see markNow: it holds still across a galaxy crossing

export const MARKER_PX = 46;        // the suit's on-screen height, in CSS pixels
export const STANDOFF_PX = 30;      // …and how far above the object its feet float
// How close to arrived counts as arrived on a followK trip. The system view's camera stops at the same fraction
// (LOCK_DONE), so a figure that kept creeping would be the only thing still moving.
const FOLLOW_DONE = 0.03;

// HOW LONG A MOVE TAKES. Long enough to read as going somewhere, short enough that it is never in the way of the
// next thing you do — the figure has arrived before a click on the world it left could matter.
// MATCHED TO THE CAMERA'S OWN TRANSITION (transitionDuration in app.js, 1.2s), and it has to be: a click on a
// star flies the camera there over that time while the figure crossed the same gap in 850ms on a different curve.
// Measured frame by frame on a real trip, that mismatch put the avatar OFF SCREEN for 39 of the 179 frames — from
// 395ms to 1024ms, almost the whole journey — because the camera leads, looks at the destination, and leaves a
// figure on a different schedule behind it. It then swooped in over the last 7% of the tween, which is what reads
// as jerk and as wrong sizing (it enters near the frustum edge, where perspective is at its most violent).
//
// Same duration AND same curve means the two hold a near-constant offset for the whole crossing, so the figure
// stays where it started on screen and genuinely travels alongside you.
export const TRAVEL_MS = 1200;
// AND HOW FAR IS TOO FAR TO ANIMATE. A hop to another galaxy is thousands of screens away: eased, it is a single
// frame of smear leaving by one edge, which reads as a glitch rather than as travel. Past this the figure simply
// appears at the far end, which is the honest picture of a move you could not have watched.
export const TRAVEL_MAX_PX = 2400;

// ACROSS A VIEW CHANGE THERE IS NOTHING TO TWEEN. The galaxy and the system are two scenes with two cameras and a
// marker each; diving into a system does not move the figure, it destroys one and builds another somewhere else on
// screen. So the seam is softened rather than faked: each marker fades up when its view starts showing it and fades
// down when it stops, instead of being switched on and off. Short enough not to feel like a transition of its own.
export const FADE_MS = 380;
// One step of that fade. Pure, so the clamps can be checked without a GPU: dt is capped the same way every other
// time-step here is (a backgrounded tab hands back one enormous frame on its return, which would make the fade a
// cut), and the result is pinned to 0..1 — an opacity outside that range is a material that renders wrong rather
// than an error anyone would see.
export function fadeStep(current, dtMs, dir) {
  const dt = Math.max(0, Math.min(100, Number(dtMs) || 0));
  const now = Number(current);
  const v = (Number.isFinite(now) ? now : 0) + (dir < 0 ? -1 : 1) * (dt / FADE_MS);
  return Math.max(0, Math.min(1, v));
}

// ADRIFT. The figure hangs in space above a world rather than standing on it, and a perfectly still one reads as a
// pasted-on icon. So it drifts: a slow rise and fall, with a little roll either side of upright. Deliberately NOT a
// full turn — a figure that goes fully upside down stops reading as a person standing in your suit and starts
// reading as debris, and it is also unreadable for the half of every cycle it spends inverted.
export const FLOAT_MS = 5200;     // one rise and fall
export const FLOAT_PX = 4;        // how far it drifts, in CSS pixels — the same units the figure's height is in
export const FLOAT_TILT = 0.075;  // radians either side of upright (~4.3°): enough to live, far short of a tumble
// The drift, as a pair of offsets for a phase in turns. Pure, so the shape can be checked without a GPU: the two
// run at DIFFERENT rates (the roll at half the bob) so they never settle into one mechanical-looking loop.
export function floatAt(phase) {
  const t = Number(phase);
  const p = Number.isFinite(t) ? t : 0;
  return { bob: Math.sin(p * Math.PI * 2) * FLOAT_PX, tilt: Math.sin(p * Math.PI) * FLOAT_TILT };
}
// WHERE IN THAT DRIFT EVERY FIGURE IS, off one wall clock. Each figure used to run its own phase from its own frame
// times, so the galaxy's and the system's were at unrelated points of the rise and fall — two figures that could
// never be swapped for one another without the swap showing, however exactly they were placed. Read off the same
// clock they are one drift, and handing the player from one view to the other is invisible. Wrapped into 0..1,
// including for a clock before the epoch, which is what the shape above is defined over.
export function floatPhase(nowMs) {
  const t = Number(nowMs);
  if (!Number.isFinite(t)) return 0;
  const p = (t / FLOAT_MS) % 1;
  return p < 0 ? p + 1 : p;
}
// CANVAS PIXELS PER SCREEN PIXEL, for a canvas drawn at `screenScale` of its own size. The system view's canvas is
// CSS-scaled during the dive (0.72 to 1), so a figure sized in its own pixels came out 28% small at the start of the
// crossing — beside a galaxy figure that was not. Anything out of range is treated as an unscaled canvas, because a
// wild factor here is a figure 20 times the size or a twentieth of it.
export function screenK(screenScale) {
  const v = Number(screenScale);
  return (Number.isFinite(v) && v >= 0.05 && v <= 20) ? 1 / v : 1;
}

// LIT BY NOTHING, AND STILL ROUND. A marker cannot borrow the scene's lighting, and this one was: buildSuit's
// default is MeshStandardMaterial, so the figure was only as visible as whatever happened to be shining on it —
// black on a night side, and black in the galaxy view, which has no lights to speak of at all. Unlit materials fix
// that but flatten the figure to a silhouette, because every face of every box then renders the same colour.
//
// So the shading is BAKED, as a brightness per vertex read off the face normal. The whole group is turned to face
// the camera, which makes local space screen space — so one fixed direction here is a key light over the viewer's
// shoulder from every angle, in both views, for ever, with no light in the scene and nothing to go wrong.
const SHADE_DIR = [-0.35, 0.82, 0.45];              // up, and over the viewer's left shoulder
const SHADE_MIN = 0.58;                             // the darkest a face may get: still plainly the player's colour
export function faceShade(nx, ny, nz) {
  const L = Math.hypot(SHADE_DIR[0], SHADE_DIR[1], SHADE_DIR[2]);
  // FINITE OR MID-GREY. A NaN here becomes a NaN vertex colour, and a box with a NaN colour renders BLACK — which
  // is precisely the failure this whole material swap exists to remove, reintroduced by one bad normal.
  const raw = (Number(nx) || 0) * SHADE_DIR[0] + (Number(ny) || 0) * SHADE_DIR[1] + (Number(nz) || 0) * SHADE_DIR[2];
  const d = Number.isFinite(raw) ? raw / (L || 1) : 0;
  // HALF-LAMBERT rather than max(0, n·L): a true cosine leaves every face pointing away at exactly SHADE_MIN, so
  // the whole back of the figure is one flat shape again. Remapping -1..1 into 0..1 keeps them distinct.
  return SHADE_MIN + (1 - SHADE_MIN) * (0.5 + 0.5 * Math.max(-1, Math.min(1, d)));
}

// WORLD UNITS PER PIXEL at a given distance from a perspective camera. The whole of the scaling, kept pure and
// out of the render loop so it can be checked without a GPU — the failure it guards against (a marker that grows
// as you zoom out, or vanishes as you zoom in) is exactly the kind that looks plausible in a screenshot.
export function unitsPerPixel(distance, fovDeg, viewportH) {
  // FINITE OR NOTHING. A non-finite distance — a camera that has not been positioned yet, a target at the origin of
  // an un-built scene — yields a non-finite scale, and a Group scaled by Infinity or NaN does not error: it simply
  // stops being drawn. A marker that silently disappears is the one failure mode this must not have, so a bad
  // distance is clamped to something drawable rather than propagated.
  const raw = Number(distance);
  const d = Math.min(1e12, Math.max(1e-9, Number.isFinite(raw) ? Math.abs(raw) : 1));
  const h = Math.max(1, Number(viewportH) || 1);
  const fov = Math.max(1e-6, (Number(fovDeg) || 50) * Math.PI / 180);
  return (2 * d * Math.tan(fov / 2)) / h;
}
// The scale a SUIT_LOCAL_HEIGHT-tall figure needs to occupy `px` pixels at that distance.
export function markerScale(distance, fovDeg, viewportH, px = MARKER_PX) {
  return unitsPerPixel(distance, fovDeg, viewportH) * Math.max(1, px) / SUIT_LOCAL_HEIGHT;
}
// THE CAMERA'S OWN EASE, character for character — cubic in-out, the curve startZoom's transition uses. It was
// smootherstep, which is a fine ease and the wrong one here: the point is not that the figure eases, it is that it
// eases IDENTICALLY to the camera carrying the player, so the two stay in the same relationship all the way across.
// Two different S-curves over the same interval still drift apart by up to a tenth of the distance in the middle,
// and a tenth of a galaxy is off screen.
//
// It keeps every property the old one was chosen for: zero first derivative at both ends (so no flick leaving or
// arriving), symmetric, and exactly half way at half time.
export function travelEase(x) {
  const t = Math.min(1, Math.max(0, Number(x) || 0));
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// Build the marker: the player's suit, held at a constant pixel height above whatever they are standing on.
// Returns a THREE.Group to add to a scene, with update() and dispose().
// `colors` is the player's saved suit palette ({base, secondary, accent} as hex ints, from their profile) and goes
// straight to the suit — it is meant to read as them, and a marker in somebody else's palette is somebody else.
export function buildHereMarker(colors = {}) {
  const group = new THREE.Group();
  group.name = 'here-marker';
  group.renderOrder = 6;
  group.frustumCulled = false;      // it is a marker: it must not disappear because its tiny bounding sphere missed

  // UNLIT, so the figure is exactly as visible in the dark as in the light — and tagged so the visor is left out of
  // the shading below and keeps the full-bright glow it has everywhere else in the game.
  const suit = buildSuit(colors, {
    castShadow: false,
    makeMaterial: (color, detail, visor) => {
      const m = new THREE.MeshBasicMaterial({
        color, vertexColors: !visor, transparent: true,   // transparent so the fade has something to drive
        polygonOffset: detail, polygonOffsetFactor: detail ? -1 : 0, polygonOffsetUnits: detail ? -8 : 0,
      });
      m.userData.visor = !!visor;
      return m;
    },
  });
  // Bake the shading in. Every box is a BoxGeometry with flat per-face normals, so this is six brightnesses per
  // box; MeshBasicMaterial multiplies its colour by them, which is the player's palette, shaded.
  suit.traverse((o) => {
    if (!o.isMesh || !o.geometry || (o.material && o.material.userData && o.material.userData.visor)) return;
    const n = o.geometry.getAttribute('normal');
    if (!n) return;
    const col = new Float32Array(n.count * 3);
    for (let i = 0; i < n.count; i++) {
      const v = faceShade(n.getX(i), n.getY(i), n.getZ(i));
      col[i * 3] = col[i * 3 + 1] = col[i * 3 + 2] = v;
    }
    o.geometry.setAttribute('color', new THREE.BufferAttribute(col, 3));
  });
  // The suit's own origin is its torso; lifting by SUIT_LOCAL_FEET puts the boot plane at y=0, so the standoff
  // below measures from its feet rather than its waist.
  // ABOUT ITS MIDDLE, NOT ITS FEET. `spin` sits at the figure's mid-height and the suit hangs from it, so the
  // cartwheel turns about the figure's centre — spinning about the anchor instead would swing it through a circle
  // a whole body-height wide and fling it off the thing it is meant to be marking. Placed this way the boot plane
  // still lands at pivot y=0 and the head still reaches y=SUIT_LOCAL_HEIGHT, so the figure's screen extent is
  // unchanged at every phase of the turn and a label clearing its head keeps clearing it.
  const HALF = SUIT_LOCAL_HEIGHT / 2;
  suit.position.y = SUIT_LOCAL_FEET - HALF;
  const spin = new THREE.Group();
  spin.position.y = HALF;
  spin.add(suit);
  const pivot = new THREE.Group();  // one thing to scale, so the figure and its standoff stay in proportion
  pivot.add(spin);

  // UPRIGHT WITH RESPECT TO THE CAMERA, not to the world — and the whole group is turned, not the figure alone.
  // The galaxy view sets camera.up to the GALAXY'S OWN NORTH (applyHoodAlign), so world +Y is not screen up there
  // and a figure built +Y-up renders tilted; from directly above or below it goes edge-on and disappears. Turning
  // the group by the camera's quaternion makes local +Y screen-up and local +Z point at the viewer, which is the
  // direction suit.js says the figure faces — so it stands up and looks at you with no extra rotation anywhere.
  // The standoff then runs up-SCREEN rather than up-world for free, on the same axis the label measures along.
  group.add(pivot);
  group.visible = false;

  const _c = new THREE.Vector3(), _trip = new THREE.Vector3();
  // THE AXIS IT HOVERS ALONG, in WORLD space, and the offset to the top of its head along the screen's own up.
  // Two different directions now, which is the whole point: the figure stands over the LOCATION's north while
  // still facing the viewer, so a label that has to clear it can no longer assume its head is a fixed number of
  // pixels above its feet — north may point sideways, or away.
  const _up = new THREE.Vector3(), _head = new THREE.Vector3(), _at = new THREE.Vector3();
  // WHERE THE FIGURE IS ACTUALLY STANDING, which during a move is not where it is going. Kept apart from `target`
  // so the destination can go on moving underneath it — worlds orbit, and a trip to one has to land on where it
  // has got to, not on where it was when the player set off.
  const _shown = new THREE.Vector3(), _from = new THREE.Vector3();
  const _camUp = new THREE.Vector3(), _p0 = new THREE.Vector3(), _p1 = new THREE.Vector3();
  let _key = null, _t = 1, _drift = 0, _fade = 0, _kFix = 1;
  // The suit's three materials, gathered once. Opacity is a MATERIAL property, and they are shared across all 18
  // boxes, so fading the figure is three writes rather than a traverse every frame.
  const _mats = [];
  suit.traverse((o) => { if (o.isMesh && o.material && _mats.indexOf(o.material) < 0) _mats.push(o.material); });
  const applyFade = () => { for (let i = 0; i < _mats.length; i++) _mats[i].opacity = _fade; };
  const stepFade = (dtMs, dir) => { _fade = fadeStep(_fade, dtMs, dir); applyFade(); return _fade; };
  // START INVISIBLE, and say so in the materials as well as in _fade. A MeshBasicMaterial defaults to opacity 1,
  // so without this the two disagree from the moment it is built: the figure is fully opaque while the fade state
  // says it has not begun, and hide() on a marker that has never been updated finds _fade already at 0 and cuts it
  // out in one frame instead of fading. Nothing draws it before the first update (group.visible is false until
  // then), so this costs nothing and makes the invariant — opacity IS _fade — true from the start.
  applyFade();

  // Place the marker over `target` (a world-space point) and hold it at a constant apparent size.
  // opts.travelKey identifies WHAT it is standing on: change it and the figure walks to the new target instead of
  // cutting to it. Anything comparable with !== works — an address string, or the body object itself.
  // opts.dtMs is this frame's elapsed milliseconds; without it a move simply completes on the next frame.
  //
  // TWO WAYS TO DRIVE THE TRIP, because the views move their cameras differently and a figure that runs its own
  // timer beside a camera on another schedule ARRIVES LATE — which is exactly what it looked like.
  //   opts.travelE   the camera's OWN eased progress, 0..1. Used verbatim, with no second easing: the figure is
  //                  then an affine function of the same parameter moving the camera, so the two cannot drift.
  //                  The galaxy view has this exactly, because a flight is a scripted run with a known t.
  //   opts.followK   the fraction of what remains the camera closes each 60fps frame. An exponential follow has
  //                  no progress to hand over, but it has a RATE — and a figure closing the same fraction of the
  //                  same gap every frame is ON the camera's curve by construction. The system view was given a
  //                  settle TIME instead and ran a timed ease against an exponential: the camera front-loads its
  //                  move and a timed ease does not, so the figure lagged at the start and caught up at the end.
  //                  That mismatch is the whole of "the avatar doesn't pan at the same rate as the camera".
  //   opts.travelMs  how long this view's camera takes, when it has neither of the above.
  // With none of them, TRAVEL_MS and the module's own clock apply, as before.
  //
  // AND TWO FOR THE HANDOVER BETWEEN VIEWS:
  //   opts.screenScale  how large this canvas is drawn on screen (the system view's is CSS-scaled during a dive), so
  //                     every pixel figure above stays a SCREEN pixel — see screenK.
  //   opts.pin          { x, y, w }: the other view's figure, in this camera's NDC, and how far this one is from it
  //                     (0) to home (1). See the pin below.
  group.userData.update = (camera, target, viewportH, opts = {}) => {
    if (!camera || !target) { group.visible = false; return; }

    // A NEW HOME. Measured in PIXELS rather than world units, because that is the thing the cap is actually about
    // — the same jump is a stroll in the system view and a smear across the universe.
    const key = opts.travelKey === undefined ? null : opts.travelKey;
    if (key !== _key) {
      const upp0 = unitsPerPixel(_c.copy(target).sub(camera.position).length(), camera.fov, viewportH);
      const tripPx = _trip.copy(target).sub(_shown).length() / Math.max(1e-12, upp0);
      // `!(tripPx <= MAX)` rather than `>`, so a NaN trip — an un-positioned target on the first frame — snaps
      // instead of easing towards nowhere for most of a second.
      // …UNLESS THE CAMERA IS CARRYING IT. A trip handed the camera's own progress (travelE) moves the view along
      // with it, so however far it goes in the world it stays on screen the whole way: the smear this cut prevents
      // cannot happen, and cutting would leave the figure waiting at the far end while the view is still on its way
      // there. (An un-positioned target — a NaN trip — still cuts: it has no way to go.)
      const carried = Number.isFinite(Number(opts.travelE)) && Number.isFinite(tripPx);
      const cut = _key === null || !group.visible || (!(tripPx <= TRAVEL_MAX_PX) && !carried);
      note(cut ? 'cut' : 'walk', { tripPx: Math.round(tripPx), first: _key === null,
                                   wasHidden: !group.visible, tooFar: !(tripPx <= TRAVEL_MAX_PX) });
      if (cut) _t = 1; else { _from.copy(_shown); _t = 0; }
      _key = key;
    }
    const givenE = Number(opts.travelE);
    if (_t < 1 && Number.isFinite(givenE)) {
      // THE CAMERA'S OWN PROGRESS, used raw. It has already been eased by whatever is moving the camera, and
      // easing it again here would bend the figure off that curve — which is the drift this exists to remove.
      //
      // BUT ONLY FORWARDS. That progress belongs to the camera's CURRENT flight, and a new flight starts its own
      // e back at 0 — so a figure that took it verbatim was dragged back down its own leg toward where it set
      // off from. That is exactly what "it goes backwards to the last target" looks like, and you can start a
      // fresh flight (a look, a crossing) without the figure's trip having ended. A trip only ever advances; a
      // genuinely new trip arrives as a new travelKey, and that resets _t to 0 above before this runs.
      _t = Math.max(_t, Math.max(0, Math.min(1, givenE)));
      _shown.lerpVectors(_from, target, _t);
    } else if (_t < 1 && Number(opts.followK) > 0 && Number(opts.followK) < 1) {
      // THE CAMERA'S OWN RATE. Closing followK of what remains each 60fps frame is the same exponential the
      // camera is running, so the two are the same curve rather than two curves of the same duration. Raised to
      // the frame count so it is rate-independent, and clamped to six frames for the same reason the timed path
      // clamps: a backgrounded tab hands back one enormous frame and the whole trip would happen inside it.
      //
      // NO travelEase HERE. The exponential IS the easing; putting a second curve on top is exactly the drift
      // this branch exists to remove.
      const frames = Math.max(0, Math.min(6, (Number(opts.dtMs) || 0) / (1000 / 60)));
      _t = Math.min(1, _t + (1 - _t) * (1 - Math.pow(1 - Number(opts.followK), frames)));
      if (1 - _t < FOLLOW_DONE) _t = 1;   // the camera stops once this close; a figure that did not would trail it
      _shown.lerpVectors(_from, target, _t);
    } else if (_t < 1) {
      // CLAMPED, because a backgrounded tab hands back one enormous frame on its return and the whole trip would
      // happen inside it — the move you came back to watch, already over.
      const ms = Math.max(1, Number(opts.travelMs) || TRAVEL_MS);
      _t = Math.min(1, _t + Math.max(0, Math.min(100, Number(opts.dtMs) || 0)) / ms);
      _shown.lerpVectors(_from, target, travelEase(_t));
    } else {
      _shown.copy(target);   // EXACTLY, not eased-to: hereClearanceFor asks whether the marker is on a given point
    }                        // by comparing positions, and a label's clearance must not depend on a last epsilon.
    _at.copy(_shown);            // the point it is standing ON, before the standoff lifts it off the surface

    // Sized from where it IS, not from where it is going, or a figure crossing towards the camera would hold the
    // scale of the far end all the way in.
    // SCALED AT THE DISTANCE IT IS DRAWN AT, which is not the distance of the point it stands on. The standoff
    // lifts the figure by upp·STANDOFF_PX along the location's north, and upp grows with distance — so far from
    // the camera that lift is LARGE in world units, and the figure ends up somewhere with a materially different
    // distance from the anchor whose distance sized it. Measured across a real trip, that alone swung the figure's
    // rendered height between 32 and 85 pixels for something that is meant to be exactly one number.
    //
    // Circular by nature (the lift depends on the scale, the scale on the lifted position), so it is solved by
    // one iteration: size at the anchor, lift by that, then re-measure at where the figure actually ended up and
    // size again. One pass is enough because the correction shrinks with the ratio it is correcting.
    // EVERY PIXEL HERE IS A SCREEN PIXEL, however the canvas is scaled — see screenK. `sk` turns them into the
    // canvas's own, which is what projection measures in.
    const sk = screenK(opts.screenScale);
    const px = Math.max(1, (opts.px || MARKER_PX) * sk);
    const standoffPx = Math.max(0, (opts.standoffPx == null ? STANDOFF_PX : opts.standoffPx) * sk);
    const upp0b = unitsPerPixel(_c.copy(_shown).sub(camera.position).length(), camera.fov, viewportH);
    if (opts.up && opts.up.isVector3 && opts.up.lengthSq() > 1e-12) _up.copy(opts.up).normalize();
    else _up.set(0, 1, 0).applyQuaternion(camera.quaternion);
    const dist = _c.copy(_shown).addScaledVector(_up, upp0b * standoffPx).sub(camera.position).length();
    const upp = unitsPerPixel(dist, camera.fov, viewportH);
    const s = upp * px / SUIT_LOCAL_HEIGHT;
    const lift = upp * standoffPx;
    pivot.scale.setScalar(s);

    // THE WHOLE MARKER TAKES THE CAMERA'S ROTATION, so the figure always faces you — that is what stops it going
    // edge-on and disappearing when you look down a pole. The cartwheel then rides INSIDE that, about the local Z
    // the camera is looking along, which is a roll in the plane of the screen: it never turns the figure away.
    group.quaternion.copy(camera.quaternion);
    // THE DRIFT, on the shared clock (floatPhase) so the figure in the other view is at the same point of it —
    // the MARKS' clock, which holds still while a crossing rebuilds the view (see markNow in bracket.js).
    _drift = floatPhase(opts.nowMs == null ? markNow() : opts.nowMs);
    const drift = floatAt(_drift);
    spin.rotation.z = drift.tilt;

    // HOVERS ALONG THE LOCATION'S OWN NORTH, not up the screen. Standing off along camera-up meant the figure slid
    // around the body as you orbited — it was never over any particular part of the world, only over the top of
    // the PICTURE of it. Given the body's axis it now sits over its pole, which is a place, and stays there while
    // you fly around it. `_up` is resolved above, because the scale correction needs it.
    //
    // The bob rides the figure's OWN up, which is screen-up because it is billboarded — so it drifts up and down
    // the screen however the location's north happens to point. The standoff itself stays in the world, below.
    pivot.position.set(0, upp * drift.bob * sk, 0);
    group.position.copy(_shown).addScaledVector(_up, lift);
    // …AND THEN RAISED UP THE SCREEN UNTIL IT CLEARS WHATEVER IT HAS TO CLEAR. The selector is a frame around the
    // OBJECT, and the figure floats above it — but "above" is a SCREEN relationship and the standoff above is a
    // WORLD one, along a north that can point anywhere. Seen nearly pole-on it foreshortens to nothing: measured
    // in the galaxy view, a 44.6px standoff put the boots 3px above the star, inside the frame they were supposed
    // to be standing over. So the caller says how far above its anchor the boots must end up, this measures what
    // the world standoff actually achieved, and only the shortfall is made up along camera-up — which is never
    // degenerate, because the figure is billboarded and camera-up is always straight up the screen. The figure
    // keeps standing over its pole; it just stops being buried in its own selector while doing it.
    // MEASURED FROM WHERE IT IS STANDING, and from nowhere else. This used to take the point the frame is
    // centred on — which mid-journey is the DESTINATION while the figure is still in transit. The shove was then
    // a function of the screen gap between the two, so it grew and shrank across the trip and the figure bowed up
    // and down on its way over instead of crossing in a straight line. Its own anchor is the one point that
    // holds still relative to it, so measuring from that makes the lift constant and the path straight.
    //
    // …UNLESS THE FRAME TRAVELS WITH IT. A caller whose frame is carried across in step with the figure — the system
    // view's "you are here", riding the camera beside it — can hand over that frame's own centre (clearFrom), and the
    // clearance is then measured from there: the bow above came from a frame that stayed at the destination, and a
    // frame moving with the figure has none. It is what keeps the boots out of a frame that grows while they cross.
    const clearPx = Math.max(0, Number(opts.clearPx) || 0) * sk;
    if (clearPx > 0) {
      _camUp.set(0, 1, 0).applyQuaternion(camera.quaternion);
      const half = viewportH * 0.5;
      _p0.copy(opts.clearFrom && opts.clearFrom.isVector3 ? opts.clearFrom : _at).project(camera);
      _p1.copy(group.position).project(camera);
      // Both behind the camera is meaningless, and one behind projects to a mirrored point — skip rather than
      // heave the figure across the frame on the strength of it.
      if (_p0.z <= 1 && _p1.z <= 1) {
        const short = clearPx - ((-_p0.y * half + half) - (-_p1.y * half + half));
        if (short > 0) group.position.addScaledVector(_camUp, upp * short);
      }
    }
    // …and the head is still MARKER_PX up the SCREEN from the feet, because the figure itself is billboarded.
    // …and the head is reported at the TOP OF THE DRIFT, not where it is this instant. A label that tracked the
    // live head would bob along with it, which is far more distracting than the figure moving under a steady one.
    _camUp.set(0, 1, 0).applyQuaternion(camera.quaternion);
    _head.copy(_camUp).multiplyScalar(upp * (px + FLOAT_PX * sk));
    // PINNED, WHILE THE OTHER VIEW HANDS THE PLAYER OVER. The galaxy and the system each draw a figure, and a
    // crossfade between them was two avatars in two places however it was tuned. What reads as ONE avatar is one
    // figure on screen at a time that MOVES: the figure taking over appears exactly where the other one stood and
    // travels from there to its own place. So the caller passes the other figure's boots, in this camera's NDC, and
    // how far along the trip is: w = 0 stands on the pin, w = 1 is home, and in between is a straight line on
    // screen. Worked out from home, which is only known here, so the two ends are this frame's and not last's.
    // Moved within its own depth plane, so nothing about how far away it is — and so how big — changes with it.
    const pin = opts.pin;
    if (pin && Number.isFinite(pin.x) && Number.isFinite(pin.y)) {
      const w0 = Number(pin.w), w = Number.isFinite(w0) ? Math.max(0, Math.min(1, w0)) : 1;
      _p0.copy(group.position).project(camera);
      if (w < 1 && _p0.z <= 1) {
        _p0.set(pin.x + (_p0.x - pin.x) * w, pin.y + (_p0.y - pin.y) * w, _p0.z).unproject(camera);
        if (Number.isFinite(_p0.x) && Number.isFinite(_p0.y) && Number.isFinite(_p0.z)) group.position.copy(_p0);
      }
    }

    // …AND THEN CORRECTED TO THE SIZE THE PROJECTION ACTUALLY PRODUCES. unitsPerPixel is exact only at the centre
    // of the frame: it is the small-angle reading, and this view runs at a 75° fov, where a marker out towards an
    // edge subtends materially more screen than the same world size does in the middle. Measured while zooming, a
    // figure meant to be 50px rendered anywhere between 52 and 75 as it drifted off-centre — the scale arithmetic
    // was right and the assumption underneath it was not.
    //
    // So the figure is measured where it is drawn and scaled by the ratio: two projections, no model of the
    // distortion, and correct for any fov and any position in the frame. Guarded because a marker at or behind the
    // near plane projects to nonsense, and a NaN scale does not error — it stops being drawn.
    _p0.copy(group.position).project(camera);
    _p1.copy(group.position).addScaledVector(_camUp, upp * px).project(camera);
    if (_p0.z <= 1 && _p1.z <= 1) {
      const shownPx = Math.abs(_p1.y - _p0.y) * 0.5 * Math.max(1, Number(viewportH) || 1);
      if (Number.isFinite(shownPx) && shownPx > 0.5) {
        const k = px / shownPx;
        if (Number.isFinite(k) && k > 0.05 && k < 20) _kFix = k;
      }
    }
    // THE LAST GOOD CORRECTION IS KEPT when this frame's cannot be measured — off screen, or across the near
    // plane, which is most of a long trip. Falling back to 1 there would drop the figure to its uncorrected size
    // for those frames and restore it on the next good one, which is a size pop built out of the very thing that
    // exists to stop size popping. The factor changes slowly with position, so yesterday's is a good answer.
    pivot.scale.multiplyScalar(_kFix);
    _head.multiplyScalar(_kFix);
    stepFade(opts.dtMs, +1);
    group.visible = true;
  };
  // STOP SHOWING IT, over FADE_MS rather than between one frame and the next. Callers used to assign
  // `visible = false`, which is the pop this replaces. Returns true once it has actually gone.
  // WHY IT JUMPED, AS A LOG. "It bounces between objects" can mean a hide/show, a cut, or a walk that looked
  // wrong, and those have different causes — so each is recorded with what it knew at the time. QA only: a ring
  // buffer of at most 60 entries, written on discontinuities and never per frame.
  group.userData.log = [];
  const note = (kind, extra) => { const L = group.userData.log;
    L.push({ kind, at: Math.round(performance.now()), ...extra });
    if (L.length > 60) L.shift(); };
  // DRIVEN, rather than run on its own clock. Two views mean two figures, and what makes them read as ONE is
  // that their opacities sum to 1 through the handover — which neither can do while each is fading at its own
  // rate from whenever it happened to be told to. hide() is still right for "this figure has no subject"; this
  // is for "the other figure is taking over, here is exactly how far along that is".
  // QA: the marker's own state, for frame-by-frame reads from the console. Pure read, no allocation per frame
  // beyond the object returned.
  group.userData.debug = () => ({ t: _t, key: _key == null ? null : String(_key), fade: +_fade.toFixed(3),
    kFix: +_kFix.toFixed(4), bob: +floatAt(_drift).bob.toFixed(3),
    shown: [_shown.x, _shown.y, _shown.z], from: [_from.x, _from.y, _from.z] });
  group.userData.setFade = (v) => {
    const f = Math.max(0, Math.min(1, Number(v) || 0));
    if (f !== _fade) { _fade = f; applyFade(); }
    group.visible = _fade > 0.002;
    return _fade;
  };
  group.userData.hide = (dtMs) => {
    if (!group.visible) return true;
    if (group.userData.log.length === 0 || group.userData.log[group.userData.log.length - 1].kind !== 'hide')
      note('hide', { fade: +_fade.toFixed(3) });
    if (stepFade(dtMs, -1) <= 0) { group.visible = false; return true; }
    return false;
  };
  // IS IT MID-TRIP? Asked by both views, because the lift they hand it must not collapse while it is crossing:
  // a selection bracket crossfading from the old subject to the new one is briefly on NEITHER, and a lift keyed
  // to "is a mark on me right now" therefore drops out and climbs back over the course of the move. Measured on a
  // real star hop: 45.1px of lift before the click, 27.3px a few frames later, back to 45.0 on arrival — a dip
  // down and up, which is exactly what it looked like.
  group.userData.travelling = () => _t < 1;
  // …AND HOW FAR ALONG, 0..1, on the same parameter its position rides. Whatever travels WITH the figure — the
  // universe view's "you are here" bracket, crossing between galaxies — reads this rather than keeping a clock of
  // its own, so the two cannot come apart.
  group.userData.progress = () => _t;

  // THE POINT IT IS STANDING ON — not group.position, which is that point plus the standoff. Anything asking
  // "is the marker on this object" has to compare against this or it will never match again.
  group.userData.at = _at;
  // THE TOP OF ITS HEAD, in world space. The one honest answer to "how much room does a label need", now that the
  // standoff runs along an axis that has nothing to do with the screen: callers project it and measure.
  group.userData.headWorld = (out) => (out || new THREE.Vector3()).copy(group.position).add(_head);
  group.userData.dispose = () => { try { suit.dispose && suit.dispose(); } catch (e) {} };
  return group;
}
